import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';

/* Stammdaten (11.10.2026): Bank aus der IBAN, Ort aus der Postleitzahl — aus den Listen des CRM-Servers.
   An den Server geht nur die Bankleitzahl bzw. die PLZ, nie die IBAN. Schlägt das Nachschlagen fehl, bleibt
   das Formular wie es ist: Der Server ergänzt BIC und Bank beim Speichern ohnehin selbst. */

const blzAus = (iban) => {
  const i = String(iban || '').replace(/\s/g, '').toUpperCase();
  return /^DE\d{10,20}$/.test(i) ? i.slice(4, 12) : '';
};

/** Trägt BIC und Bankname ein, sobald die Bankleitzahl in einer deutschen IBAN vollständig ist.
    `uebernehmen({ bic, kreditinstitut })` — liefert die erkannte Bank (oder null) für einen Hinweis. */
export function useBankAusIban(iban, uebernehmen) {
  const [bank, setBank] = useState(null);
  const blz = blzAus(iban);
  const ruf = useRef(uebernehmen);
  ruf.current = uebernehmen;
  useEffect(() => {
    if (!blz) { setBank(null); return undefined; }
    let aktuell = true;
    api.get(`/stammdaten/bank/${blz}`).then((r) => {
      if (!aktuell) return;
      setBank(r.data);
      ruf.current({ bic: r.data.bic || '', kreditinstitut: r.data.bank || '' });
    }).catch(() => { if (aktuell) setBank(null); });
    return () => { aktuell = false; };
  }, [blz]);
  return bank;
}

/** Schlägt den Ort zur PLZ vor. Genau ein Ort: wird eingetragen, solange das Feld leer ist oder noch der
    letzte Vorschlag drinsteht — eine Handeingabe wird nie überschrieben. Mehrere Orte: Liste zur Auswahl. */
export function useOrtAusPlz(plz, ort, setOrt) {
  const [orte, setOrte] = useState([]);
  const vorschlag = useRef('');
  const stand = useRef({ ort, setOrt });
  stand.current = { ort, setOrt };
  const p = String(plz || '').trim();
  useEffect(() => {
    if (!/^\d{5}$/.test(p)) { setOrte([]); return undefined; }
    let aktuell = true;
    api.get(`/stammdaten/ort/${p}`).then((r) => {
      if (!aktuell) return;
      const liste = r.data.orte || [];
      setOrte(liste);
      const jetzt = String(stand.current.ort || '');
      if (liste.length === 1 && (!jetzt.trim() || jetzt === vorschlag.current) && jetzt !== liste[0]) {
        vorschlag.current = liste[0];
        stand.current.setOrt(liste[0]);
      }
    }).catch(() => { if (aktuell) setOrte([]); });
    return () => { aktuell = false; };
  }, [p]);
  return orte;
}

/** Auswahl bei mehreren Orten zu einer PLZ — kleine Knöpfe unter dem Feld. */
export function OrtVorschlaege({ orte, aktuell, wahl }) {
  if (!orte || orte.length < 2) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1" data-testid="ort-vorschlaege">
      {orte.slice(0, 12).map((o) => (
        <button key={o} type="button" onClick={() => wahl(o)}
          className={`px-2 py-0.5 rounded-full border text-xs ${aktuell === o ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted/50'}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

/** Hinweiszeile unter der IBAN: welche Bank erkannt wurde. */
export function BankHinweis({ bank }) {
  if (!bank) return null;
  return <p className="text-xs text-muted-foreground mt-1" data-testid="bank-hinweis">Erkannt: {bank.bank}{bank.bic ? ` · ${bank.bic}` : ''}</p>;
}
